import { useState, useEffect, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { requestEmailVerificationCode, User } from '@elevator/ui';
import { reportError } from '../lib/errors';

const COOLDOWN_DURATION_SECONDS = 60;

/**
 * Custom hook for handling resend verification code functionality.
 * Manages cooldown timer, loading state, and error handling.
 *
 * @param currentUser - The current user object
 * @returns Object containing resend state and handler function
 */
export const useResendVerificationCode = (currentUser: User) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isResending, setIsResending] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  // Cooldown timer effect
  useEffect(() => {
    if (cooldownSeconds > 0) {
      const timer = setTimeout(() => {
        setCooldownSeconds(cooldownSeconds - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldownSeconds]);

  const handleResend = useCallback(async () => {
    if (cooldownSeconds > 0 || isResending) {
      return;
    }

    setIsResending(true);
    try {
      await requestEmailVerificationCode(process.env.AUTH_ENDPOINT);
      enqueueSnackbar('Verification code sent!', {
        variant: 'success',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
      // Set cooldown
      setCooldownSeconds(COOLDOWN_DURATION_SECONDS);
    } catch (err) {
      reportError(err, {
        metaData: {
          operation: 'cognito requestEmailVerificationCode',
        },
        user: {
          username: currentUser.username,
        },
      });
      enqueueSnackbar(
        `Could not send verification code, please make sure you're logged in`,
        {
          variant: 'error',
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          },
        },
      );
    } finally {
      setIsResending(false);
    }
  }, [cooldownSeconds, isResending, currentUser, enqueueSnackbar]);

  return {
    isResending,
    cooldownSeconds,
    handleResend,
  };
};
